import React from 'react';
const { useState, useEffect } = React;
import { expensesCall, expenseErrorText, fmtMoney, fmtDay, openExpense } from '../../expensesClient.js';
import { REVIEW_REASON_TEXT } from '../../movements.js';
import { LABELS } from '../../expenseSchema.js';
import ForecastView from './ForecastView.jsx';
import FinanceObservations from './FinanceObservations.jsx';

// Finanzas › Movimientos (only the owner; expenses.mts checks it on every call; agreed with Ale on 04-11-2026): what
// was registered in a period, per currency (EUR, USD, BOB, never added or converted). Not a result, profit or EBITDA,
// and not the money in any account. Business and personal apart; the expense's scope apart from where the money came
// from. Each figure opens its records; a part that could not be read is said, never shown as zero.

const muted = { fontSize: 12, color: 'var(--globant-muted)' };
const box = { border: '1px solid var(--globant-border)', borderRadius: 10, padding: 14, display: 'grid', gap: 8, background: 'var(--globant-card, #fff)', minWidth: 0 };
const chip = (on) => ({ border: `1px solid ${on ? 'var(--globant-green)' : 'var(--globant-border)'}`, background: on ? 'var(--teal-dim)' : 'transparent', color: on ? '#155C54' : 'var(--globant-text)', borderRadius: 20, padding: '5px 11px', fontSize: 12, fontWeight: 600, cursor: 'pointer' });
const pad = (n) => String(n).padStart(2, '0');
const lastDay = (y, m) => new Date(Date.UTC(y, m, 0)).getUTCDate();
function periodOf(kind) {
  const d = new Date(); const y = d.getUTCFullYear(); const m = d.getUTCMonth() + 1;
  if (kind === 'prev') { const pm = m === 1 ? 12 : m - 1; const py = m === 1 ? y - 1 : y; return { from: `${py}-${pad(pm)}-01`, to: `${py}-${pad(pm)}-${lastDay(py, pm)}` }; }
  if (kind === 'quarter') { const q0 = Math.floor((m - 1) / 3) * 3 + 1; return { from: `${y}-${pad(q0)}-01`, to: `${y}-${pad(q0 + 2)}-${lastDay(y, q0 + 2)}` }; }
  if (kind === 'year') return { from: `${y}-01-01`, to: `${y}-12-31` };
  return { from: `${y}-${pad(m)}-01`, to: `${y}-${pad(m)}-${lastDay(y, m)}` };
}
const PERIODS = [['month', 'Este mes'], ['prev', 'Mes anterior'], ['quarter', 'Este trimestre'], ['year', 'Este año']];
const MONTHS = ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sept', 'oct', 'nov', 'dic'];
const monthName = (ym) => { const [y, m] = ym.split('-').map(Number); return `${MONTHS[m - 1]} ${y}`; };

/** A figure that opens its records. `value` null = could not be read (never zero). */
function Figure({ label, value, cur, items, note, onOpen, strong = false, tone }) {
  const unknown = value === null || value === undefined;
  const amount = unknown ? null : typeof value === 'number' ? value : value.amount;
  const list = items || (unknown || typeof value === 'number' ? null : value.items);
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, alignItems: 'baseline', fontSize: 13.5, borderBottom: '1px dashed var(--globant-border)', paddingBottom: 6 }}>
      <span style={{ minWidth: 0 }}>{label}{note && <span style={{ display: 'block', ...muted }}>{note}</span>}</span>
      {unknown ? <span style={{ ...muted, color: '#9A6212' }}>no disponible</span>
        : list?.length ? <button type="button" className="action-btn" style={{ fontVariantNumeric: 'tabular-nums', fontWeight: strong ? 800 : 600, fontSize: strong ? 16 : 13.5, color: tone }} onClick={() => onOpen({ label, cur, items: list })}>{fmtMoney(amount, cur)}</button>
        : <span style={{ fontVariantNumeric: 'tabular-nums', fontWeight: strong ? 800 : 600, fontSize: strong ? 16 : 13.5, color: tone }}>{fmtMoney(amount, cur)}</span>}
    </div>
  );
}

function Records({ open, onClose }) {
  if (!open) return null;
  return (
    <div style={{ ...box, borderColor: 'var(--globant-green)' }} role="region" aria-label={`Registros: ${open.label}`}>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap' }}><b style={{ fontSize: 14 }}>{open.label} · {open.cur}</b><button type="button" className="action-btn" onClick={onClose}>Cerrar</button></div>
      {open.items.map((it, i) => (
        <div key={`${it.id}-${it.paymentId || i}`} style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'baseline', fontSize: 13, borderTop: '1px solid var(--globant-border)', paddingTop: 6 }}>
          <span style={{ flex: '1 1 260px', minWidth: 0 }}>
            {it.day || it.dueOn ? `${fmtDay(it.day || it.dueOn)} · ` : ''}{it.concept || it.payer || 'Pago recibido'}{it.supplier ? ` · ${it.supplier}` : ''}{it.payer && it.concept ? ` · ${it.payer}` : ''}
            {it.paidFrom && <span style={muted}> · {LABELS.paidFrom[it.paidFrom] || it.paidFrom}</span>}{it.receipt ? <span style={muted}> · {it.receipt}</span> : null}
          </span>
          <span style={{ fontVariantNumeric: 'tabular-nums', fontWeight: 600 }}>{fmtMoney(it.amount, open.cur)}</span>
          {it.scope && <button type="button" className="action-btn" style={{ fontSize: 11 }} onClick={() => openExpense(it.scope, it.id)}>Abrir el gasto</button>}
          {it.quoteId && <button type="button" className="action-btn" style={{ fontSize: 11 }} onClick={() => window.dispatchEvent(new CustomEvent('oike:navigate', { detail: { page: 'quotes', quoteId: it.quoteId } }))}>Abrir la cotización</button>}
        </div>
      ))}
    </div>
  );
}

/** Two tabs: «Registrado» (what was registered in a period; the default, so existing links open the same screen) and «Previsión». */
export default function MovementsHub() {
  const [tab, setTab] = useState('registered');
  return (
    <div className="page-content" style={{ padding: 20, maxWidth: 1100, display: 'grid', gap: 14 }}>
      <div>
        <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: 1.2, textTransform: 'uppercase', color: 'var(--globant-muted)' }}>Finanzas · solo vos</div>
        <h1 style={{ margin: '2px 0 0' }}>Movimientos</h1>
      </div>
      <FinanceObservations />
      <div role="tablist" aria-label="Vista" style={{ display: 'flex', gap: 6 }}>
        {[['registered', 'Registrado'], ['forecast', 'Previsión']].map(([k, l]) => <button key={k} type="button" role="tab" aria-selected={tab === k} style={chip(tab === k)} onClick={() => setTab(k)}>{l}</button>)}
      </div>
      {tab === 'registered' ? <Registered /> : <ForecastView />}
    </div>
  );
}

function Registered() {
  const [kind, setKind] = useState('month');
  const [scope, setScope] = useState('business');
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [open, setOpen] = useState(null);
  const period = periodOf(kind);
  const load = () => { setError(''); setData(null); setOpen(null); expensesCall('movements', period).then(setData).catch(e => setError(expenseErrorText(e))); };
  useEffect(load, [kind]); // eslint-disable-line react-hooks/exhaustive-deps
  const withScope = (items, sc) => (items || []).map(i => ({ ...i, scope: sc }));
  const openWith = (sc) => (x) => setOpen({ ...x, items: sc ? withScope(x.items, sc) : x.items });

  return (
    <div style={{ display: 'grid', gap: 14 }}>
      <p style={{ ...muted, fontSize: 13, margin: 0, maxWidth: '75ch' }}>Lo registrado en Oike en el período, por moneda y sin convertir. No es un resultado ni el saldo de tus cuentas.</p>
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }} role="group" aria-label="Período">
        {PERIODS.map(([k, l]) => <button key={k} type="button" style={chip(kind === k)} aria-pressed={kind === k} onClick={() => setKind(k)}>{l}</button>)}
        <span style={{ ...muted, alignSelf: 'center' }}>{fmtDay(period.from)} – {fmtDay(period.to)}</span>
      </div>
      <div role="tablist" aria-label="Ámbito" style={{ display: 'flex', gap: 6 }}>
        <button type="button" role="tab" aria-selected={scope === 'business'} style={chip(scope === 'business')} onClick={() => { setScope('business'); setOpen(null); }}>Negocio</button>
        <button type="button" role="tab" aria-selected={scope === 'personal'} style={chip(scope === 'personal')} onClick={() => { setScope('personal'); setOpen(null); }}>Personal</button>
      </div>
      {error && <div role="alert" style={box}>No se pudo leer el resumen: {error} No significa que no haya movimientos. <button type="button" className="action-btn" onClick={load}>Reintentar</button></div>}
      {!data && !error && <div style={muted}>Cargando…</div>}
      {data && scope === 'business' && <Business data={data} onOpen={openWith(null)} onOpenExpenses={openWith('business')} reload={load} />}
      {data && scope === 'personal' && <Personal data={data} onOpen={openWith('personal')} reload={load} />}
      <Records open={open} onClose={() => setOpen(null)} />
    </div>
  );
}

function Business({ data, onOpen, onOpenExpenses, reload }) {
  const b = data.business;
  const curs = Object.keys(b.currencies);
  return <>
    {(b.incomeError || b.expensesError) && <div role="alert" style={{ ...box, borderColor: '#9A6212' }}>
      {b.incomeError && <span>No se pudieron leer los cobros: esas cifras se muestran como «no disponible», nunca como cero.</span>}
      {b.expensesError && <span>No se pudieron leer los gastos del negocio: esas cifras se muestran como «no disponible», nunca como cero.</span>}
      <div><button type="button" className="action-btn" onClick={reload}>Reintentar</button></div>
    </div>}
    {b.monthsWithout.length > 0 && <div style={{ ...muted, fontSize: 13 }}>Sin gastos del negocio registrados en {b.monthsWithout.map(monthName).join(', ')}: puede faltar información (no está confirmado que no haya habido gastos).</div>}
    {!curs.length ? <div style={{ ...box, ...muted }}>Sin movimientos registrados en este período.</div> : (
      <div style={{ display: 'grid', gap: 12, gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))' }}>
        {curs.map(cur => { const c = b.currencies[cur]; return (
          <div key={cur} style={box}>
            <b style={{ fontSize: 12, letterSpacing: 1, color: 'var(--globant-muted)' }}>{cur}</b>
            <Figure label="Cobros recibidos" cur={cur} value={c.received} onOpen={onOpen} note={c.refunds?.amount ? `Devoluciones: ${fmtMoney(c.refunds.amount, cur)}` : 'Cada pago una vez, aunque cubra varias cuotas'} />
            <Figure label="Pagos de gastos del negocio" cur={cur} value={c.paid} onOpen={onOpenExpenses} />
            {c.byOrigin && <div style={{ display: 'grid', gap: 4, paddingLeft: 12 }}>
              <Figure label="· con dinero del negocio" cur={cur} value={c.byOrigin.business} onOpen={onOpenExpenses} />
              <Figure label="· con dinero personal" cur={cur} value={c.byOrigin.personal} onOpen={onOpenExpenses} note={c.byOrigin.personal.amount ? 'Gastos del negocio pagados con tu dinero: no salieron de una cuenta del negocio.' : null} />
              <Figure label="· con otro origen" cur={cur} value={c.byOrigin.other} onOpen={onOpenExpenses} />
            </div>}
            <Figure label="Cobros menos pagos registrados" cur={cur} value={c.difference} strong tone={c.difference === null ? undefined : c.difference < 0 ? '#B4362C' : '#1E7B4F'} note="Lo registrado en el período; no es el saldo de tus cuentas" onOpen={onOpen} />
            <Figure label="Por cobrar" cur={cur} value={c.toCollect} onOpen={onOpen} note={c.overdueToCollect?.amount ? `Vencido: ${fmtMoney(c.overdueToCollect.amount, cur)}` : null} />
            {c.overdueToCollect?.amount > 0 && <Figure label="· vencido por cobrar" cur={cur} value={c.overdueToCollect} onOpen={onOpen} tone="#B4362C" />}
            <Figure label="Por pagar (a hoy)" cur={cur} value={c.toPay} onOpen={onOpenExpenses} />
            {c.overdueToPay?.amount > 0 && <Figure label="· vencido por pagar" cur={cur} value={c.overdueToPay} onOpen={onOpenExpenses} tone="#B4362C" />}
            {c.unapplied?.amount > 0 && <Figure label="Cobrado sin aplicar a una cuota" cur={cur} value={c.unapplied} onOpen={onOpen} />}
          </div>
        ); })}
      </div>
    )}
    <ReviewList title="Incompletos o por revisar (negocio)" review={b.review} scope="business" extra={[
      ...b.unresolvedPlans.map(p => ({ key: p.quoteId, text: `Plan de cobro por resolver: ${p.name || 'cotización'} (${p.currency || ''})`, quoteId: p.quoteId })),
      ...b.legacyWithoutAmount.map(p => ({ key: `l-${p.quoteId}`, text: `Registro anterior sin importe: ${p.name || 'cotización'} (no se cuenta como dinero)`, quoteId: p.quoteId })),
    ]} />
  </>;
}

function Personal({ data, onOpen, reload }) {
  const p = data.personal;
  const curs = Object.keys(p.currencies || {});
  return <>
    <span style={{ ...muted, color: 'var(--area-private, #6B5CA5)', fontWeight: 600 }}>Gastos personales: privados y separados del negocio. Nunca entran en sus cifras.</span>
    {p.error && <div role="alert" style={box}>No se pudieron leer los gastos personales. No significa que no haya. <button type="button" className="action-btn" onClick={reload}>Reintentar</button></div>}
    {!p.error && p.monthsWithout.length > 0 && <div style={{ ...muted, fontSize: 13 }}>Sin gastos personales registrados en {p.monthsWithout.map(monthName).join(', ')}.</div>}
    {!p.error && (!curs.length ? <div style={{ ...box, ...muted }}>Sin gastos personales registrados en este período.</div> : (
      <div style={{ display: 'grid', gap: 12, gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))' }}>
        {curs.map(cur => { const c = p.currencies[cur]; return (
          <div key={cur} style={box}>
            <b style={{ fontSize: 12, letterSpacing: 1, color: 'var(--globant-muted)' }}>{cur}</b>
            <Figure label="Pagos de gastos personales" cur={cur} value={c.paid} onOpen={onOpen} />
            <div style={{ display: 'grid', gap: 4, paddingLeft: 12 }}>
              <Figure label="· con dinero personal" cur={cur} value={c.byOrigin.personal} onOpen={onOpen} />
              <Figure label="· con dinero del negocio" cur={cur} value={c.byOrigin.business} onOpen={onOpen} note={c.byOrigin.business.amount ? 'Gastos personales pagados con dinero del negocio: revisalo; no se mueve nada solo.' : null} />
              <Figure label="· con otro origen" cur={cur} value={c.byOrigin.other} onOpen={onOpen} />
            </div>
            <Figure label="Por pagar (a hoy)" cur={cur} value={c.pending} onOpen={onOpen} />
            {c.overdue.amount > 0 && <Figure label="· vencido" cur={cur} value={c.overdue} onOpen={onOpen} tone="#B4362C" />}
          </div>
        ); })}
      </div>
    ))}
    {!p.error && <ReviewList title="Incompletos o por revisar (personal)" review={p.review} scope="personal" extra={[]} />}
  </>;
}

function ReviewList({ title, review = [], scope, extra = [] }) {
  if (!review.length && !extra.length) return <div style={{ ...box, ...muted }}>{title}: nada pendiente de revisar.</div>;
  return (
    <div style={box}>
      <b style={{ fontSize: 14 }}>{title}</b>
      {review.map(r => (
        <div key={r.id} style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'baseline', fontSize: 13 }}>
          <span style={{ flex: '1 1 260px' }}>{r.concept} · {r.supplier} · {fmtMoney(r.amount, r.currency)} <span style={muted}>({r.reasons.map(x => REVIEW_REASON_TEXT[x] || x).join('; ')})</span></span>
          <button type="button" className="action-btn" style={{ fontSize: 11 }} onClick={() => openExpense(scope, r.id)}>Abrir el gasto</button>
        </div>
      ))}
      {extra.map(x => (
        <div key={x.key} style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'baseline', fontSize: 13 }}>
          <span style={{ flex: '1 1 260px' }}>{x.text}</span>
          {x.quoteId && <button type="button" className="action-btn" style={{ fontSize: 11 }} onClick={() => window.dispatchEvent(new CustomEvent('oike:navigate', { detail: { page: 'quotes', quoteId: x.quoteId } }))}>Abrir la cotización</button>}
        </div>
      ))}
    </div>
  );
}
